/* eslint-disable no-nested-ternary */
import React, { useCallback, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { flexbox, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import { selectBillingProfiles } from 'redux-api/reselect/billing';
import ContentLoader from 'theme/content-loader';
import BillingProfileCard from 'theme/billing-profile-card';
import Text from 'theme/text';
import BtnLink from 'theme/buttons/btn-link';
import Plus from 'theme/icons/plus';
import ModalConfirm from 'theme/modal/confirm';
import translations from 'translations';

const Section = styled.div`
  width: 100%;
  max-width: 2100px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${flexbox}
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: ${themeGet('space.m')}px 0;
  padding: ${themeGet('space.s')}px;
  background: ${themeGet('colors.primary_100')};
  border: 1px solid ${themeGet('colors.primary_200')};
  border-radius: 12px;
  overflow: auto;

  ${layout}
`;

const WrapEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 220px;
`;

function BillingProfiles({ summaryVariant, ids }) {
  const { push } = useRouter();

  const [modalNewProfileVisible, setModalNewProfileVisible] = useState(false);

  const { languageCode } = useSelector((state) => state.config);
  const { isProfilesWaiting } = useSelector((state) => state.billing);
  const billingProfiles = useSelector(selectBillingProfiles);

  const handleNewProfile = useCallback(() => {
    if (billingProfiles.total === 0) {
      return push('/billing/profile');
    }

    return setModalNewProfileVisible(true);
  }, [push, billingProfiles.total]);

  const handleNewProfileCancel = useCallback(() => {
    setModalNewProfileVisible(false);
  }, []);

  const handleNewProfileOk = useCallback(() => {
    setModalNewProfileVisible(false);
    push('/billing/profile');
  }, [push]);

  const filteredBillingProfiles = useMemo(() => {
    if (!summaryVariant) return billingProfiles;

    const { profiles } = billingProfiles;

    const filtered = profiles.filter((profile) => Array.isArray(ids) && ids.includes(profile.id));

    return {
      total: filtered.length,
      totalDraft: filtered.filter((profile) => profile.isDraft).length,
      profiles: filtered,
    };
  }, [ids, billingProfiles, summaryVariant]);

  return (
    <Section>
      <Row justifyContent="space-between">
        <Row>
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
            {summaryVariant
              ? translations[languageCode]['Associated profiles']
              : translations[languageCode]['Billing Profiles']}
          </Text>
          <Text pl="xxs" fontSize="h3" lineHeight="h3" fontWeight="bold" color="primary_400">
            {filteredBillingProfiles.total}
          </Text>

          <Text
            pl="xxs"
            fontSize="display"
            lineHeight="display"
            fontWeight="medium"
            color="grey_400"
          >
            {`(${filteredBillingProfiles.totalDraft} ${translations[languageCode].Draft})`}
          </Text>
        </Row>

        {!summaryVariant && (
          <Row>
            <BtnLink
              style={{
                height: 38,
              }}
              label={translations[languageCode]['Create new']}
              icon={<Plus />}
              onClick={handleNewProfile}
            />
          </Row>
        )}
      </Row>
      <CardsContainer minHeight="220px">
        {isProfilesWaiting ? (
          <ContentLoader
            width={808}
            height={220}
            lines={[
              {
                x: 0,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 220,
              },
              {
                x: 408,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 220,
              },
            ]}
          />
        ) : filteredBillingProfiles.profiles.length === 0 ? (
          <WrapEmpty>
            <Text
              pl="xxs"
              fontSize="pSmall"
              lineHeight="pSmall"
              fontWeight="normal"
              color="primary_600"
            >
              {summaryVariant
                ? translations[languageCode]["You don't have a “Billing Profile” associated"]
                : translations[languageCode]["You don't have a “Billing Profile” yet"]}
            </Text>
          </WrapEmpty>
        ) : (
          filteredBillingProfiles.profiles.map((profile) => (
            <BillingProfileCard
              key={profile.id}
              style={{
                mr: 'xs',
                maxWidth: '400px',
                border: profile.isDefault ? '3px solid' : '1px solid',
                borderColor: profile.isDefault ? 'primary_400' : 'primary_200',
              }}
              {...profile}
              subTitle={translations[languageCode][profile.billingModeCode]}
              languageCode={languageCode}
              extraInfo
            />
          ))
        )}
      </CardsContainer>

      <ModalConfirm
        isVisible={modalNewProfileVisible}
        title={translations[languageCode]['You already have a billing profile']}
        message={
          translations[languageCode][
            'We do not recommend having multiple. Please always use your default profile unless strictly necessary'
          ]
        }
        onClickCancel={handleNewProfileCancel}
        onClickOk={handleNewProfileOk}
        okLabel={translations[languageCode]['Create another profile']}
      />
    </Section>
  );
}

export default BillingProfiles;
